import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { type BulkSpotQuote } from '../domain/BulkSpotQuote'
import type { SubmitBulkSpotQuoteParams } from '../infra/BulkSpotQuoteMapper'
import { getBulkSpotQuote } from '../use-cases/getBulkSpotQuote'
import { submitBulkSpotQuote } from '../use-cases/submitBulkSpotQuote'

type State = {
  fetchCount: number
  bulkSpotQuote?: BulkSpotQuote
  bulkSpotQuoteError?: Error
  bulkSpotQuoteStatus: RequestStatus
}

const initialState = {
  fetchCount: 0,
  bulkSpotQuoteStatus: RequestStatus.Initial,
}

export function useBulkSpotQuote() {
  const [bulkSpotQuoteState, setBulkSpotQuoteState] = useState<State>(initialState)
  const [submitRatesStatus, setSubmitRatesStatus] = useState<RequestStatus>(RequestStatus.Initial)

  const fetchBulkSpotQuote = useCallback(async (id: string, abortController?: AbortController) => {
    try {
      setBulkSpotQuoteState({
        fetchCount: 0,
        bulkSpotQuote: undefined,
        bulkSpotQuoteError: undefined,
        bulkSpotQuoteStatus: RequestStatus.Pending,
      })

      const bulkSpotQuoteResult = await getBulkSpotQuote(id, abortController)

      setBulkSpotQuoteState(previous => ({
        ...previous,
        fetchCount: previous.fetchCount + 1,
        bulkSpotQuote: bulkSpotQuoteResult,
        bulkSpotQuoteStatus: RequestStatus.Done,
      }))
    } catch (error) {
      if (!abortController?.signal.aborted) {
        setBulkSpotQuoteState(previous => ({
          ...previous,
          bulkSpotQuoteError: error as Error,
          bulkSpotQuoteStatus: RequestStatus.Error,
        }))
      }
    }
  }, [])

  const refetchBulkSpotQuote = useCallback(
    async (id: string, abortController?: AbortController) => {
      try {
        const bulkSpotQuoteResult = await getBulkSpotQuote(id, abortController)

        setBulkSpotQuoteState(previous => ({
          ...previous,
          fetchCount: previous.fetchCount + 1,
          bulkSpotQuote: bulkSpotQuoteResult,
        }))
      } catch (error) {
        if (!abortController?.signal.aborted) {
          setBulkSpotQuoteState(previous => ({
            ...previous,
            bulkSpotQuoteError: error as Error,
            bulkSpotQuoteStatus: RequestStatus.Error,
          }))
        }
      }
    },
    []
  )

  const submitRates = useCallback(async (bulkSpotQuote: SubmitBulkSpotQuoteParams) => {
    setSubmitRatesStatus(RequestStatus.Pending)

    try {
      await submitBulkSpotQuote(bulkSpotQuote)

      setSubmitRatesStatus(RequestStatus.Done)
    } catch (error) {
      setSubmitRatesStatus(RequestStatus.Error)

      throw error
    }
  }, [])

  return {
    ...bulkSpotQuoteState,
    submitRatesStatus,
    fetchBulkSpotQuote,
    refetchBulkSpotQuote,
    submitRates,
  }
}
